import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useSelectedProgram, UNASSIGNED_PROGRAM } from '../../contexts/SelectedProgramContext';
import { Sheet } from './ui';

// Lets a coach/admin pick which program/season their Groups, Feed, Calendar
// and Messages are scoped to — see SelectedProgramContext. Falls back to the
// plain "Academy Portal" label (via `fallback`) until the academy has
// actually created a program, so an academy that never adopts programs sees
// no change at all.
//
// `variant="subline"` renders the mobile header's understated text trigger;
// `variant="boxed"` renders the desktop sidebar's bordered button. Both open
// the same Sheet — visual difference only, not behavioural.
export default function ProgramSwitcher({ fallback, variant = 'subline' }) {
  const { t } = useTranslation();
  const { programs, hasPrograms, selectedProgramId, selectProgram } = useSelectedProgram();
  const [open, setOpen] = useState(false);

  if (!hasPrograms || !selectedProgramId) return fallback ?? null;

  const options = [
    ...programs.map((p) => ({ id: p.id, name: p.name })),
    { id: UNASSIGNED_PROGRAM, name: t('academy:programSwitcher.unassigned', 'Unassigned') },
  ];
  const currentName = options.find((o) => o.id === selectedProgramId)?.name || '';

  return (
    <>
      {variant === 'boxed' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ap-tap"
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', borderRadius: 8,
            background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.07)', width: '100%', textAlign: 'left',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <p className="ap-ovl" style={{ color: 'rgba(255,255,255,.35)' }}>{t('academy:programSwitcher.programme', 'Programme')}</p>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentName}</p>
          </div>
          <ChevronDownIcon style={{ width: 13, height: 13, strokeWidth: 2.2, color: 'rgba(255,255,255,.45)', flexShrink: 0 }} />
        </button>
      ) : (
        <button type="button" onClick={() => setOpen(true)} style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: '100%' }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,.52)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentName}</span>
            <ChevronDownIcon style={{ width: 11, height: 11, strokeWidth: 2.4, color: 'rgba(255,255,255,.4)', flexShrink: 0 }} />
          </span>
        </button>
      )}

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={t('academy:programSwitcher.sheetTitle', 'Programme & season')}
        sub={t('academy:programSwitcher.sheetSubtitle', 'Everything in the portal — calendar, feed, groups — is scoped to this selection.')}
      >
        <div className="ap-ovl" style={{ margin: '4px 0 8px' }}>{t('academy:programSwitcher.programme', 'Programme')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 14 }}>
          {options.map((o) => {
            const active = o.id === selectedProgramId;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => { selectProgram(o.id); setOpen(false); }}
                className="ap-tap"
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderRadius: 10, textAlign: 'left',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  background: active ? 'var(--accent-light)' : 'var(--surface)',
                }}
              >
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: active ? 'var(--accent-ink)' : 'var(--t1)' }}>{o.name}</span>
                {active && <CheckIcon style={{ width: 16, height: 16, strokeWidth: 2.2, color: 'var(--accent-ink)' }} />}
              </button>
            );
          })}
        </div>
      </Sheet>
    </>
  );
}
